import React, {
    useContext,
    useEffect,
    useState,
    useRef
} from 'react';
import { useNavigate, useParams } from "react-router";
import { useQuery, useMutation } from '@tanstack/react-query';

import { refetchBasketData, getBasketData, NotificationContext, handleError } from '../router'

import * as CONSTANTS from "../constants";
import * as QUERIES from "../query-functions";
import * as UTILS from "../utils";

import InstanceEventSummary from '../components/instance-event-summary';
import PurchaseByPrice from "./purchase-by-price";
import TicketQuantityButtons from "../components/ticket-quantity-buttons"
import BestAvailableButton from './best-available-button';
import LoadingIndicator from '../components/loading-indicator';

let unreserved_ticketType_ID = null;

export default function BookOnline() {

    const {notification, setNotification} = useContext(NotificationContext);
    const { instanceId } = useParams();
    let navigate = useNavigate();

    const { data: basketData, isPending: basketDataPending, isFetching: basketDataFetching } = getBasketData();

    const [isReserved, setIsReserved] = useState(null);
    const [ticketQuantity, setTicketQuantity] = useState(1);
    const [bandsByPrice, setBandsByPrice] = useState(null);

    const addTickets = useMutation({
        mutationFn: () => QUERIES.addTickets(ticketQuantity, unreserved_ticketType_ID),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: (data) => {
            navigate(`/book-online/${instanceId}/edit-tickets`);
            refetchBasketData();
        },
    })

    const { data: planData } = useQuery({
        queryKey: ['planData'],
        queryFn: QUERIES.getPlanData,
        staleTime: 86400000,
        refetchOnWindowFocus: false,
    });

    const {
        data: priceListData,
    } = useQuery({
        queryKey: ["priceListData", CONSTANTS.INSTANCE_ID],
        queryFn: () => QUERIES.getInstancePriceList(CONSTANTS.INSTANCE_ID),
        staleTime: 86400000,
        refetchOnWindowFocus: false,
        enabled: !isReserved
    });

    const {
        data: bandsData,
    } = useQuery({
        queryKey: ["bandsData"],
        queryFn: QUERIES.getBands,
        staleTime: 86400000,
    });

    useEffect(() => {
        if (!bandsData) return;

        // Get the band id's from bandData and add it to bandsByPrice to be used by addBestAvailable()
        const updatedBandsArray = UTILS.getBandsByPrice(bandsData);
        setBandsByPrice(updatedBandsArray);
    }, [bandsData]);

    useEffect(() => {
        if (!planData) return;

        if (planData.type == "Unreserved") {
            setIsReserved(false);

            if (priceListData) {
                // Check for priceband that is unreserved and the default ticket type
                let default_ticket = priceListData.prices?.find((price) => price.priceBand.name == "Unreserved" && price.ticketType?.name == CONSTANTS.DEFAULT_TICKET_TYPE);

                if (!default_ticket) {
                    // If "Unreserved" name doesn't exist just look for default ticket type
                    default_ticket = priceListData.prices?.find((price) => price.ticketType?.name == CONSTANTS.DEFAULT_TICKET_TYPE);
                }

                // If no default ticket type just return the first in the array of prices
                unreserved_ticketType_ID = default_ticket ? default_ticket.ticketType?.id : priceListData.prices[0].ticketType?.id;
            }
            
        } else {
            setIsReserved(true);

            if (!CONSTANTS.HAS_BEST_AVAILABLE) {
                navigate(`/book-online/${instanceId}/select-seats`);
            }
        }
    }, [planData, priceListData])

    if (isReserved == null) return <LoadingIndicator type="page" />

    return (
        <div className="booking-pathway__book-online">
            <div className="booking-pathway__select-quantity">
                <InstanceEventSummary />
                <div className="booking-pathway__select-quantity--content">

                    <div className="booking-pathway__ticket-quantity">
                        <h2 className='booking-pathway__header'>How many tickets</h2>
                        <TicketQuantityButtons ticketQuantity={ticketQuantity} setTicketQuantity={setTicketQuantity} />
                    </div>

                    <div className="booking-pathway__footer booking-pathway__select-quantity--footer">

                        { isReserved ? (
                            <>
                                { CONSTANTS.HAS_BEST_AVAILABLE ?
                                    <>
                                        <BestAvailableButton ticketQuantity={ticketQuantity} />
                                        <PurchaseByPrice bandsByPrice={bandsByPrice} ticketQuantity={ticketQuantity} />
                                    </>
                                : null}
                                
                                <button className='btn btn--select-seats' type="button" onClick={() => navigate(`/book-online/${instanceId}/select-seats`)}>
                                    Or skip to choose my own seats
                                </button>
                            </>
                        ) : (
                            <button className='btn' 
                                    type="button" 
                                    disabled={addTickets.isPending || basketDataPending || basketDataFetching} 
                                    onClick={() => addTickets.mutateAsync()}>
                                Continue
                                {addTickets.isPending || basketDataPending || basketDataFetching &&
                                    <LoadingIndicator />
                                }
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}